import { useReducer, useState ,  useContext } from "react";
import { Box as RBox, DNA } from "@dna/react";
import {
    AppMaxIcon,
    AppMinIcon,
    FilterIcon,
    TickIcon
} from "@communicare/medications/Components/Icons";
import { Box, Grid, IconButton, makeStyles } from "@material-ui/core";
import { PharmacyRequestList } from "../../pharmacy-medications/PharmacyRequestList";
import { PharmacyErrors, SelectableMedication, usePharmacyMedicationsLoader } from "../../pharmacy-medications/usePharmacyMedications";
import { FlatButton } from "../../common/FlatButton";
import { DetailsPaneState } from '@communicare/medications/Components/DetailsPane';
import { PharmacyMedicationsCollapsed } from '../../pharmacy-medications/PharmacyMedicationsCollapsed';
import { RenderIf } from "../../common/render-if/RenderIf";
import { IconProps } from "@communicare/medications/Components/StyledIcon";
import { MedicationOverviewLoader } from "@communicare/medications/MedicationOverview/MedicationOverview";
import { CommunicareWrapper } from "../../communicare-mount/MountCommunicare";
import { CourseType, MedicationState, PackagingType, medicationsService} from "../../../services/medicationmanagement/medications";
import MedicationErrorDialogBox, { IOKDialogBoxProps } from "../../common/dialogbox/MedicationErrorDialogBox";
import { AdjustMedicationInfoEditor } from "../AdjustMedicationInfoEditor";
import { AppBorderColor, AppBorderSize } from '../../common/style/emed.style';
import { IdentityContext } from "../../auth/UserIdentity";
import { toast } from "../../common/toast";

const useStyles = makeStyles({
    header: {
        backgroundColor: DNA.tokens.color.gray["100"],
        borderColor: DNA.tokens.color.gray["300"],
        borderWidth: "1px",
        borderStyle: "solid"
    },
    actionButtonContainer: {
        display: "flex",
        flexDirection: "row-reverse",
        width: "100%"
    },
    title: {
        alignSelf: "center"
    },
    confirmButton: {
        alignSelf: "center",
        marginRight: 14
    },
    mainContent: {
        flexFlow: "nowrap"
    }
})

const ToggleIcon = (props: { paneState: DetailsPaneState } & IconProps) => {
    switch (props.paneState) {
        case DetailsPaneState.ParentView:
            return <AppMinIcon {...props} />
        case DetailsPaneState.ChildView:
            return <AppMaxIcon {...props} />

    }
}

enum PharmacyMedicationsComponentActions {
    ToggleFilter,
    ToggleView,
    SelectMedication,
    OpenDoseTimesEditor,
    CloseDoseTimesEditor,
}

type payload = {
    medicationAuthoringSystem: string;
    medicationAuthoringSystemForeignKey: string;
    encounterAuthoringSystem: string;
    encounterAuthoringSystemForeignKey: string;
} | null;
interface UIState {
    filterOpen: boolean;
    dosageTimesEditorState: 'Open' | 'Close';
    paneState: DetailsPaneState;
    selectedMedication: payload;
}

function togglePaneState(paneState: DetailsPaneState) {
    return paneState === DetailsPaneState.ChildView ?
        DetailsPaneState.ParentView : DetailsPaneState.ChildView;
}
function reducer(state: UIState, action: { type: PharmacyMedicationsComponentActions, payload: payload }): UIState {

    switch (action.type) {
        case PharmacyMedicationsComponentActions.ToggleFilter:
            return {
                ...state,
                filterOpen: !state.filterOpen
            };
        case PharmacyMedicationsComponentActions.ToggleView:
            return {
                ...state,
                paneState: togglePaneState(state.paneState),
                selectedMedication: action.payload
            }
        case PharmacyMedicationsComponentActions.SelectMedication:
            return {
                ...state,
                paneState: DetailsPaneState.ChildView,
                selectedMedication: action.payload ? { ...action.payload } : null
            };
        case PharmacyMedicationsComponentActions.OpenDoseTimesEditor:
            return {
                ...state,
                paneState: DetailsPaneState.ChildView,
                dosageTimesEditorState: "Open"
            };
        case PharmacyMedicationsComponentActions.CloseDoseTimesEditor:
            return {
                ...state,
                paneState: DetailsPaneState.ChildView,
                dosageTimesEditorState: "Close"
            };
    }
}

const createKey = (payload: payload) => {
    if (payload) {
        let rndVal = Date.now() + Math.random().toString(36).substring(2, 9);
        let { medicationAuthoringSystem, medicationAuthoringSystemForeignKey } = payload;
        return `${medicationAuthoringSystem}_${medicationAuthoringSystemForeignKey}_${rndVal}`;
    }
    return 'medication';
}

const okDialogprops: IOKDialogBoxProps = {
    title: "There was a problem marking this change as actioned",
    content: `We have experienced an issue confirming the medication changes have been actioned. Please try again.`,
};
export interface IPharmacyMedicationsProps {
    onConfirm: (medicationId?: string, data?: SelectableMedication[]) => void;
    residentAuthoringSystem: string;
    residentAuthoringSystemForeignKey: string;
}

export const PharmacyMedicationsChart = (props: IPharmacyMedicationsProps) => {
    let classes = useStyles();
    let authoringSystem = props.residentAuthoringSystem;
    let authoringSystemForeignKey = props.residentAuthoringSystemForeignKey;
    const [actionedState, setActionedState] = useState(false);
    let { loading, error, data, getEncounterInfo, toggleMedicationState, reload } = usePharmacyMedicationsLoader(authoringSystem || '', authoringSystemForeignKey || '');
    let [uiState, uiDispatch] = useReducer(reducer, {
        selectedMedication: null,
        paneState: DetailsPaneState.ParentView,
        filterOpen: false,
        dosageTimesEditorState: 'Close'
    });
    let ctx = useContext(IdentityContext);

    const resetAndToggleMedicationState: typeof toggleMedicationState = (a, b) => {
        setActionedState(false);
        toggleMedicationState(a, b);
    }
    let canConfirm = (items: SelectableMedication[]) => {
        return items?.some(x => !x.confirmed && x.status == MedicationState.New || x.status == MedicationState.Ceased);
    }

    let canDoMedicationSync = () => {
        let email = ctx?.userInfo?.email;
        let emailToCheck = 'Medpoint.Pharmacy@telstrahealth.com';
        if(email?.toLowerCase() === emailToCheck.toLowerCase())
           return true;

         return false;
    }

    let triggerMedSync = (residentAuthoringSystem: string, residentAuthoringSystemForeignKey:string) => {
         medicationsService.triggerMedicationSync(
            residentAuthoringSystem,
            residentAuthoringSystemForeignKey
        )
         .then(_ => {
            toast.showMessage("Background job has been triggered. Please check associated tables for status")
         })
         .catch(_ => {
            toast.showError("Could not trigger sync to Clinical")
         });
    }

    const PaneToggleIcon = (props: IconProps) => {
        return <ToggleIcon paneState={uiState.paneState} {...props} />
    }

    const isSelectedMedication = (item: SelectableMedication) => {
        return item.authoringSystem === uiState.selectedMedication?.medicationAuthoringSystem && item.authoringSystemForeignKey === uiState.selectedMedication?.medicationAuthoringSystemForeignKey
    }
    const getMedicationName = (medicationAuthoringSystem: string, medicationAuthoringSystemForeignKey: string): string => {
        return data?.meds.find(x => x.authoringSystem === medicationAuthoringSystem && x.authoringSystemForeignKey === medicationAuthoringSystemForeignKey)?.product?.name || '';
    };
    const getMedSummary = (medicationAuthoringSystem: string, medicationAuthoringSystemForeignKey: string): { courseType?: CourseType, medicationId: string, packingType?: PackagingType } => {

        const medSummary = {
            courseType: data?.meds.find(x => x.authoringSystem === medicationAuthoringSystem && x.authoringSystemForeignKey === medicationAuthoringSystemForeignKey)?.courseType || undefined,
            medicationId: data.medicationId ? data.medicationId : "",
            packagingType: data?.meds.find(x => x.authoringSystem === medicationAuthoringSystem && x.authoringSystemForeignKey === medicationAuthoringSystemForeignKey)?.packagingType || undefined,
        }
        return medSummary;
    };
    function handleonactionedState() {
        setActionedState(true);
    }

    return (
        <Grid className={classes.mainContent} container spacing={0} >
            <Grid style={{
                minWidth: "312px"
            }} item xs={uiState.paneState === DetailsPaneState.ParentView ? 12 : 3}>

                <Grid container className={classes.header}>
                    <Grid item xs className={classes.title}>
                        <Box fontWeight={600} fontSize={20} pl={1.5}>Medication Chart</Box>
                    </Grid>
                    <Grid item xs={4}>
                        <Box className={classes.actionButtonContainer}>

                            <RBox css={{
                                borderStartColor: AppBorderColor,
                                borderStartWidth: AppBorderSize
                            }}>
                                <IconButton aria-label="close" title="Collapse" onClick={() => {
                                    uiDispatch({
                                        type: PharmacyMedicationsComponentActions.ToggleView,
                                        payload: null
                                    });
                                }}>
                                    <PaneToggleIcon />
                                </IconButton>
                            </RBox>

                            <RBox css={{
                                borderStartColor: AppBorderColor,
                                borderStartWidth: AppBorderSize
                            }}>
                                <IconButton aria-label="close" title="Filter" onClick={() => {
                                    uiDispatch({
                                        type: PharmacyMedicationsComponentActions.ToggleFilter,
                                        payload: null
                                    });
                                }}>
                                    <FilterIcon />
                                </IconButton>
                            </RBox>                        
                            <RenderIf if={uiState.paneState === DetailsPaneState.ParentView}>
                                <>
                                   <RenderIf if={canDoMedicationSync()}>
                                        <Box className={classes.confirmButton}>
                                                <FlatButton  onPress={() => triggerMedSync(props.residentAuthoringSystem, props.residentAuthoringSystemForeignKey)} startIcon={function noRefCheck() {
                                                    return <TickIcon
                                                        backgroundColour={DNA.tokens.color.turquoise.mid}
                                                        color="#fff"
                                                        style={{
                                                            minHeight: "1px",
                                                            height: "12px",
                                                            minWidth: "1px",
                                                            width: "12px",
                                                        }}
                                                    />
                                                }
                                                } kind="secondary" css={{
                                                    height: 32
                                                }}>
                                                    <Box fontWeight={DNA.tokens.font.weight.semiBold}>
                                                        Sync to Clinical Manager
                                                    </Box>
                                                </FlatButton>
                                        </Box>
                                   </RenderIf>
                                    <Box className={classes.confirmButton}>
                                        <FlatButton isDisabled={!canConfirm(data.meds)} onPress={() => props.onConfirm(data.medicationId, data.meds)} startIcon={function noRefCheck() {
                                            return <TickIcon
                                                backgroundColour={DNA.tokens.color.turquoise.mid}
                                                color="#fff"
                                                style={{
                                                    minHeight: "1px",
                                                    height: "12px",
                                                    minWidth: "1px",
                                                    width: "12px",
                                                }}
                                            />
                                        }
                                        } kind="secondary" css={{
                                            height: 32
                                        }}>
                                            <Box fontWeight={DNA.tokens.font.weight.semiBold}>
                                                Confirm actioned
                                            </Box>
                                        </FlatButton>
                                    </Box>
                                </>
                            </RenderIf>
                        </Box>
                    </Grid>
                </Grid>
                <RenderIf
                    if={uiState.paneState === DetailsPaneState.ParentView}
                    else={
                        <>
                            {uiState.dosageTimesEditorState === 'Open' && <AdjustMedicationInfoEditor
                                medicationAuthoringSystem={uiState.selectedMedication?.medicationAuthoringSystem || ''}
                                medicationAuthoringSystemForeignKey={uiState.selectedMedication?.medicationAuthoringSystemForeignKey || ''}
                                medicationName={
                                    getMedicationName(uiState.selectedMedication?.medicationAuthoringSystem || '', uiState.selectedMedication?.medicationAuthoringSystemForeignKey || '')
                                }
                                medSummary={getMedSummary(uiState.selectedMedication?.medicationAuthoringSystem || '', uiState.selectedMedication?.medicationAuthoringSystemForeignKey || '')}
                                onConfirmed={() => {
                                    uiDispatch({
                                        type: PharmacyMedicationsComponentActions.CloseDoseTimesEditor,
                                        payload: null
                                    });
                                    reload()

                                    uiDispatch({
                                        type: PharmacyMedicationsComponentActions.SelectMedication,
                                        payload: {
                                            medicationAuthoringSystem: uiState.selectedMedication?.medicationAuthoringSystem || '',
                                            medicationAuthoringSystemForeignKey: uiState.selectedMedication?.medicationAuthoringSystemForeignKey || '',
                                            encounterAuthoringSystem: uiState.selectedMedication?.encounterAuthoringSystem || '',
                                            encounterAuthoringSystemForeignKey: uiState.selectedMedication?.encounterAuthoringSystemForeignKey || ''
                                        }
                                    });
                                    reload()
                                }}
                                onCancel={() => {
                                    uiDispatch({
                                        type: PharmacyMedicationsComponentActions.CloseDoseTimesEditor,
                                        payload: null
                                    });
                                }}
                            />}
                            <PharmacyMedicationsCollapsed
                                medications={data?.meds}
                                onMedicationActioned={resetAndToggleMedicationState}
                                isSelected={isSelectedMedication}
                                onRowSelected={(authoringSystem, authoringSystemForeignKey, encounterAuthoringSystem, encounterAuthoringSystemForeignKey) => {
                                    uiDispatch({
                                        type: PharmacyMedicationsComponentActions.SelectMedication,
                                        payload: {
                                            medicationAuthoringSystem: authoringSystem,
                                            medicationAuthoringSystemForeignKey: authoringSystemForeignKey,
                                            encounterAuthoringSystem: encounterAuthoringSystem,
                                            encounterAuthoringSystemForeignKey: encounterAuthoringSystemForeignKey
                                        }
                                    });
                                }}
                                onStartEditingDosageTimes={() => {
                                    uiDispatch({
                                        type: PharmacyMedicationsComponentActions.OpenDoseTimesEditor,
                                        payload: null
                                    });
                                }}
                                confirmActionButton={
                                    <FlatButton isDisabled={!canConfirm(data.meds)} onPress={() => props.onConfirm(data.medicationId, data.meds)} startIcon={function noRefCheck() {
                                        return <TickIcon
                                            color="#fff"
                                            style={{
                                                minHeight: "1px",
                                                height: "12px",
                                                minWidth: "1px",
                                                width: "12px",
                                            }}
                                        />
                                    }
                                    } kind="primary" css={{
                                        height: 40,
                                        width: "100%"
                                    }}>
                                        <Box fontWeight={DNA.tokens.font.weight.semiBold}>
                                            Confirm actioned
                                        </Box>
                                    </FlatButton>
                                }
                            />
                        </>

                    }
                >
                    <>
                        <MedicationErrorDialogBox open={error === PharmacyErrors.MedicationActionError && !actionedState} dialogBoxProps={okDialogprops} onactionedState={handleonactionedState} />

                        <PharmacyRequestList
                            data={data.meds}
                            medicationorderId={data.medicationId}
                            onMedicationActioned={resetAndToggleMedicationState}
                            onRowSelected={(authoringSystem, authoringSystemForeignKey, encounterAuthoringSystem, encounterAuthoringSystemForeignKey) => {
                                uiDispatch({
                                    type: PharmacyMedicationsComponentActions.SelectMedication,
                                    payload: {
                                        medicationAuthoringSystem: authoringSystem,
                                        medicationAuthoringSystemForeignKey: authoringSystemForeignKey,
                                        encounterAuthoringSystem: encounterAuthoringSystem,
                                        encounterAuthoringSystemForeignKey: encounterAuthoringSystemForeignKey
                                    }
                                });
                            }}
                        />
                    </>
                </RenderIf>
            </Grid>
            <RenderIf if={uiState.paneState === DetailsPaneState.ChildView && !!uiState.selectedMedication}>
                <Grid item>
                    <CommunicareWrapper>
                        <MedicationOverviewLoader
                            key={createKey(uiState.selectedMedication)}
                            patientMedicationId={
                                {
                                    authoringSystem: uiState.selectedMedication?.medicationAuthoringSystem || '',
                                    authoringSystemForeignKey: uiState.selectedMedication?.medicationAuthoringSystemForeignKey || ''
                                }
                            }
                            patient={
                                {
                                    authoringSystem,
                                    authoringSystemForeignKey
                                }
                            }
                            service={
                                {
                                    authoringSystem: uiState.selectedMedication?.encounterAuthoringSystem || '',
                                    authoringSystemForeignKey: uiState.selectedMedication?.encounterAuthoringSystemForeignKey || ''
                                }
                            }
                            getDefaultViewingRight={() => null}
                            onUnmount={() => { }}
                            onFormUnsaved={() => { }}
                            onFormSaved={() => { }}
                            onUpdate={() => { }}
                            controlledMedicationHandler={
                                {
                                    controlledMedicationDialogOpen: false,
                                    controlledMedications: [],
                                    processingLogin: false,
                                    onCancelControlDialog: () => { },
                                    showControlledMedicationDialog: () => { },
                                    handleLoginFlow: () => { },
                                    requiresAuthentication: () => false
                                }
                            }
                            encounter={
                                {
                                    authoringSystem: uiState.selectedMedication?.encounterAuthoringSystem || '',
                                    authoringSystemForeignKey: uiState.selectedMedication?.encounterAuthoringSystemForeignKey || ''
                                }
                            }
                        />
                    </CommunicareWrapper>
                </Grid>
            </RenderIf>

        </Grid>
    );
};
